class PriceRange extends HTMLElement {
  connectedCallback() {
    this.abort_controller?.abort();
    this.abort_controller = new AbortController();
    this.price_inputs = [...this.querySelectorAll('.price-range--input')];
    this.price_range_buttons = [...this.querySelectorAll('.price-range--button')];
    this.price_range_line = this.querySelector('.price-range--line');
    if (this.price_inputs.length !== 2 || !this.price_range_line) return;
    this.max = Number(this.price_inputs[1].max);
    this.step = Number(this.price_inputs[1].step) || 0.01;
    if (!Number.isFinite(this.max) || this.max <= 0) return;
    const options = {signal: this.abort_controller.signal};
    this.price_inputs.forEach((input, index) => {
      input.addEventListener('change', () => this.commitInput(index), options);
      input.addEventListener('keydown', event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.commitInput(index);
        }
      }, options);
    });
    this.price_range_buttons.forEach((button, index) => {
      button.setAttribute('role', 'slider');
      button.addEventListener('keydown', event => {
        const change = {ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1, PageDown: -10, PageUp: 10}[event.key];
        if (change === undefined && !['Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const [min, max] = this.values();
        let value = (index ? max : min) + (change || 0) * this.step;
        if (event.key === 'Home') value = index ? min : 0;
        if (event.key === 'End') value = index ? this.max : max;
        this.price_inputs[index].value = String(value);
        this.commitInput(index);
      }, options);
      button.addEventListener('pointerdown', event => {
        if (event.button !== 0) return;
        event.preventDefault();
        button.focus();
        button.setPointerCapture(event.pointerId);
        this.dragging = {index, id: event.pointerId};
        this.movePointer(event);
      }, options);
      button.addEventListener('pointermove', event => {
        if (this.dragging?.id === event.pointerId) this.movePointer(event);
      }, options);
      button.addEventListener('pointerup', event => {
        if (this.dragging?.id !== event.pointerId) return;
        this.movePointer(event);
        this.dragging = null;
        this.commitInput(index);
      }, options);
      button.addEventListener('pointercancel', () => {
        this.dragging = null;
        this.readAndRender();
      }, options);
    });
    window.addEventListener('theme:priceRange:clear', () => {
      this.price_inputs[0].value = '0';
      this.price_inputs[1].value = String(this.max);
      this.synchronize(0, this.max);
      if (this === document.querySelector('mb-price-range')) window.trigger('theme:navigation:loadNewProducts');
    }, options);
    this.resize_observer = new ResizeObserver(() => this.readAndRender());
    this.resize_observer.observe(this.price_range_line);
    this.readAndRender();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.readAndRender(), {once: true, signal: this.abort_controller.signal});
  }
  disconnectedCallback() {
    this.abort_controller?.abort();
    this.resize_observer?.disconnect();
  }
  values() {
    const rawMin = Number.parseFloat(this.price_inputs[0].value.replace(',', '.'));
    const rawMax = Number.parseFloat(this.price_inputs[1].value.replace(',', '.'));
    return [Math.max(0, Math.min(this.max, Number.isFinite(rawMin) ? rawMin : 0)), Math.max(0, Math.min(this.max, Number.isFinite(rawMax) ? rawMax : this.max))];
  }
  commitInput(index) {
    let [min, max] = this.values();
    if (min > max) {
      if (index === 0) max = min;
      else min = max;
    }
    this.synchronize(min, max);
    window.trigger('theme:navigation:loadNewProducts');
  }
  synchronize(min, max) {
    document.querySelectorAll('mb-price-range').forEach(range => {
      const inputs = range.querySelectorAll('.price-range--input');
      if (inputs.length !== 2 || inputs[0].name !== this.price_inputs[0].name || inputs[1].name !== this.price_inputs[1].name) return;
      inputs[0].value = String(min);
      inputs[1].value = String(max);
      if (typeof range.readAndRender === 'function') range.readAndRender();
    });
    this.readAndRender();
  }
  movePointer(event) {
    const rect = this.price_range_line.getBoundingClientRect();
    if (!rect.width) return;
    const index = this.dragging.index;
    const [min, max] = this.values();
    let value = Math.round(Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)) * this.max / this.step) * this.step;
    value = index ? Math.max(min, value) : Math.min(max, value);
    this.price_inputs[index].value = value.toFixed(2);
    this.readAndRender();
  }
  readAndRender() {
    if (!this.price_inputs || !this.max) return;
    const [min, max] = this.values();
    const width = this.price_range_line.getBoundingClientRect().width;
    this.price_range_buttons.forEach((button, index) => {
      const value = index ? max : min;
      button.setAttribute('aria-valuemin', String(index ? min : 0));
      button.setAttribute('aria-valuemax', String(index ? this.max : max));
      button.setAttribute('aria-valuenow', String(value));
      if (width) button.style.transform = `translateX(${index ? -(width - value / this.max * width) : value / this.max * width}px)`;
    });
    if (width) {
      const fill = this.price_range_line.firstElementChild;
      fill.style.margin = `0 ${100 - max / this.max * 100}% 0 ${min / this.max * 100}%`;
      fill.style.width = `${Math.max(0, max - min) / this.max * 100}%`;
    }
    window.trigger('theme:priceRange:renderActive', {current_min: min, current_max: max, max: this.max});
  }
}

customElements.define('mb-price-range', PriceRange);
